// src/lib/cfg-api.js — client de l'endpoint /api/cfg (Lot B, admin-only).
//
// Lot B : la config cfg passe du KV fbz8-cfg à la table cfg_config derrière un
// endpoint serveur admin. GET renvoie le blob (claudeKey expurgé) ; POST écrit
// le blob entier (table + miroir KV). Erreurs remontées (toast).
//
// ── JETON DE VERSION (2026-08-05) ───────────────────────────────────────────
//
// L'enregistrement envoie le blob ENTIER tel que cet onglet l'a chargé. Sans
// garde, un onglet resté ouvert écrase en silence toute modification faite
// ailleurs entre-temps — y compris des montants de caution ou des taux de
// commission. Ce module retient donc la version chargée et la renvoie au
// serveur, qui refuse (409) si la base a bougé. Détail : lib/cfg-version.js.

import { supabase } from '../supabase'
import { lireJsonOuRefuser } from './reponse-json'

// Version de la config que CET onglet détient. Volontairement au niveau du
// module et non dans l'état React : c'est exactement la sémantique voulue
// (« ce que ce navigateur a chargé »), et ça évite la fermeture obsolète qui
// guette dès qu'on fait transiter une valeur par un état + des props.
let jetonVersion = ''

/** Message affiché quand la SOURCE est enregistrée mais pas la copie ménage. */
export const MESSAGE_MIROIR_OBSOLETE =
  'Configuration enregistrée. ⚠️ Mais la copie lue par vos agents de ménage ' +
  "n'a pas pu être mise à jour : ils peuvent voir des informations périmées " +
  '(adresses, codes, horaires). Réessayez d’enregistrer dans un moment.'

// ── LES PHRASES ────────────────────────────────────────────────────────────
// Écrites pour être lues par Dimitri, pas par un développeur : pas de « JSON »,
// pas de « parse », pas de code HTTP. Elles disent TROIS choses, dans cet
// ordre : ce que je n'ai pas pu faire · ce que ça change à l'écran · que rien
// n'est perdu et quoi faire.

/** Lecture — montrée en bandeau, l'écran n'est JAMAIS laissé vide en silence. */
export const MESSAGE_CFG_ILLISIBLE =
  "Je n'ai pas pu lire ta configuration : ce que le serveur a renvoyé " +
  "n'en était pas une. Les écrans peuvent s'afficher vides ou incomplets " +
  "— rien n'est perdu, recharge la page."

/** Écriture — on ne PEUT PAS dire si l'enregistrement est passé : ne pas mentir. */
export const MESSAGE_CFG_ECRITURE_ILLISIBLE =
  "Je n'ai pas pu lire la réponse du serveur : je ne sais pas si " +
  "l'enregistrement est passé. Ne réessaie pas à l'aveugle — recharge la " +
  "page pour voir ce qui est vraiment enregistré."

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// Le verdict « ce n'est pas du JSON » vit dans src/lib/reponse-json.js —
// quatre recopies de cette fonction laissaient passer une page HTML en 200
// pour un blob vide, et vidaient les écrans sans un mot.
async function parseOrThrow(res, fallback, messageIllisible) {
  return lireJsonOuRefuser(res, fallback, messageIllisible)
}

export async function fetchCfg() {
  const res = await fetch('/api/cfg', { headers: await authHeaders() })
  const cfg = await parseOrThrow(res, 'Lecture cfg échouée', MESSAGE_CFG_ILLISIBLE)
  jetonVersion = res.headers?.get?.('x-cfg-version') || ''
  return cfg
}

/**
 * Écrit le blob cfg.
 *
 * @returns {Promise<{cfg: object, avertissement: string|null}>} le blob persisté
 *   (claudeKey expurgé côté serveur) + un avertissement à afficher le cas
 *   échéant (enregistrement réussi mais copie ménage périmée).
 * @throws {Error} message serveur — dont le conflit de version (409), dont le
 *   texte explique quoi faire. Les panneaux le montrent tel quel et CONSERVENT
 *   les modifications à l'écran : rien n'est perdu.
 */
export async function saveCfgRemote(cfg) {
  const res = await fetch('/api/cfg', {
    method: 'POST',
    headers: { ...(await authHeaders()), 'x-cfg-version': jetonVersion },
    body: JSON.stringify(cfg || {}),
  })
  // 🔴 NE JAMAIS mémoriser le jeton avant d'avoir vérifié le succès. Rafraîchir
  // le jeton sur un 409 rendrait un second clic sur « Enregistrer » gagnant —
  // c'est-à-dire exactement l'écrasement silencieux que cette garde existe pour
  // empêcher. Sur conflit, la seule issue est de recharger la page.
  const persisted = await parseOrThrow(
    res,
    'Enregistrement cfg échoué',
    MESSAGE_CFG_ECRITURE_ILLISIBLE,
  )
  jetonVersion = res.headers?.get?.('x-cfg-version') || ''
  return {
    cfg: persisted,
    avertissement:
      res.headers?.get?.('x-cfg-miroir') === 'obsolete' ? MESSAGE_MIROIR_OBSOLETE : null,
  }
}

// Réservé aux tests : la version détenue est un état de module, elle doit
// pouvoir être posée et lue explicitement pour que les tests prouvent quelque
// chose plutôt que de constater un `undefined`.
export const _jeton = {
  lire: () => jetonVersion,
  poser: (v) => { jetonVersion = v || '' },
}
